/**
 * Profile Validation Utilities
 * 
 * Zod schemas for validating profile-related inputs
 */

import { z } from 'zod';
import type { UpdateProfileInput, ChangeEmailInput, ChangePasswordInput } from '../types/profile.types';
import { isValidAustralianMobile, normalizePhoneNumber } from './phone-format';

/**
 * Validation schema for updating profile information
 */
export const updateProfileSchema = z.object({
  firstName: z.string().max(100, 'First name must be 100 characters or less').optional(),
  lastName: z.string().max(100, 'Last name must be 100 characters or less').optional(),
  phoneNumber: z.string()
    .refine((val) => {
      if (!val || val.trim() === '') return true; // Empty is valid (optional)
      return isValidAustralianMobile(val);
    }, 'Phone must be a valid Australian mobile number (04XX XXX XXX)')
    .transform((val) => {
      // Normalize phone number to standard format before saving
      if (!val || val.trim() === '') return '';
      return normalizePhoneNumber(val);
    })
    .optional()
    .or(z.literal('')),
  dateOfBirth: z.string()
    .refine((val) => {
      if (!val || val === '') return true;
      const date = new Date(val);
      const now = new Date();
      const age = now.getFullYear() - date.getFullYear();
      const monthDiff = now.getMonth() - date.getMonth();
      const dayDiff = now.getDate() - date.getDate();
      
      // Calculate exact age
      let exactAge = age;
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        exactAge--;
      }
      
      return date <= now && exactAge >= 13 && exactAge <= 120;
    }, 'Date of birth must be valid, not in future, and age must be 13-120')
    .optional()
    .or(z.literal('')),
  countryOfResidence: z.string().min(1, 'Country of residence is required').optional(),
});

/**
 * Validation schema for changing email address
 */
export const changeEmailSchema = z.object({
  newEmail: z.string().email('Invalid email address'),
});

/**
 * Validation schema for changing password
 */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

/**
 * Validate update profile input
 */
export function validateUpdateProfileInput(input: UpdateProfileInput): { valid: boolean; errors?: z.ZodError } {
  const result = updateProfileSchema.safeParse(input);
  if (result.success) {
    return { valid: true };
  }
  return { valid: false, errors: result.error };
}

/**
 * Validate change email input
 */
export function validateChangeEmailInput(input: ChangeEmailInput): { valid: boolean; errors?: z.ZodError } {
  const result = changeEmailSchema.safeParse(input);
  if (result.success) {
    return { valid: true };
  }
  return { valid: false, errors: result.error };
}

/**
 * Validate change password input
 */
export function validateChangePasswordInput(input: ChangePasswordInput): { valid: boolean; errors?: z.ZodError } {
  const result = changePasswordSchema.safeParse(input);
  if (result.success) {
    return { valid: true };
  }
  return { valid: false, errors: result.error };
}

